import {ArtfactKind} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import * as fs from 'fs';
import moment from 'moment';
import {Observable, Observer, of} from 'rxjs';
import {Beneficiario} from './slip/lib/beneficiario';
import {GeradorDeBoleto} from './slip/lib/boleto/gerador-de-boleto';
import {GeradorDeDigitoPadrao} from './slip/lib/boleto/gerador-de-digito-padrao';
import {bancos, Boleto} from './slip/lib/boletoUtils';
import {Datas} from './slip/lib/datas';
import {Endereco} from './slip/lib/endereco';
import {FormatacoesUtils} from './slip/lib/formatacoesUtils';
import {Pagador} from './slip/lib/pagador';
import {WritableBufferStream} from './slip/lib/writeable.buffer';
import {dados} from './yaml/temp_remessa_1';
import {YamlGenerate} from './yaml/yaml.generate';
import {concatMap} from 'rxjs/operators';

@HookClass('bank')
export class BankHook extends BaseHook {

    @Hook('6f8dfdee-a3eb-4ef0-a40c-e4da6cba51d4')
    public slip(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        if (inputArtifact.object.data.bankCode == 237)
            return this.boletoBradesco(inputArtifact);

        const createBoleto = () => {
            const pagador = createPagador();
            const beneficiario = createBeneficiario();
            const instrucoes = createInstrucoes();

            return Boleto.novoBoleto()
                .comDatas(Datas.novasDatas()
                    .comVencimento(inputArtifact.object.data.dueAt)
                    .comProcessamento(inputArtifact.object.data.generateAt)
                    .comDocumento(inputArtifact.object.data.generateAt))
                .comBeneficiario(beneficiario)
                .comPagador(pagador)
                .comBanco(new bancos.Santander())
                .comValorBoleto(parseFloat(inputArtifact.object.data.ammount)) // Apenas duas casas decimais
                .comNumeroDoDocumento(1001)
                .comEspecieDocumento('DM') // Duplicata de Venda Mercantil
                .comLocaisDePagamento(['Em qualquer banco até o vencimento'])
                .comInstrucoes(instrucoes);
        };

        const createPagador = () => {
            const enderecoPagador = Endereco.novoEndereco();
            enderecoPagador.primeiraLinha = `${inputArtifact.object.data.ownerAddress1}`;
            enderecoPagador.segundaLinha = `${inputArtifact.object.data.ownerAddress2}`;

            return Pagador.novoPagador()
                .comNome(`${inputArtifact.object.data.ownerName}`)
                .comRegistroNacional(`${inputArtifact.object.data.ownerDocument}`)
                .comEndereco(enderecoPagador);
        };

        const createBeneficiario = () => {
            const enderecoBeneficiario = Endereco.novoEndereco()
                .comLogradouro('RUA RIACHUELO, 75')
                .comBairro('CENTRO')
                .comCidade('RIO DE JANEIRO')
                .comUf('RJ')
                .comCep('20230-010');

            return Beneficiario.novoBeneficiario()
                .comNome('CONSELHO NACIONAL DO BRASIL DA S.S.V.P')
                .comRegistroNacional('34127563000167')
                .comCarteira('101')
                .comAgencia('3201')
                .comCodigoBeneficiario('2507390')
                .comDigitoCodigoBeneficiario('8')
                .comNossoNumero(inputArtifact.object.data.ourNumber.toString().padStart(11, '0')) // 11 -digitos // "00000005752"
                .comDigitoNossoNumero(GeradorDeDigitoPadrao.mod11(inputArtifact.object.data.ourNumber.toString(), {
                    de: [0, 1, 10, 11],
                    para: 0
                }))
                .comEndereco(enderecoBeneficiario);
        };

        const createInstrucoes = () => {
            const instrucoes = [];
            instrucoes.push(`${inputArtifact.object.data.paymentInstruction}`);
            return instrucoes;
        };

        const boleto = createBoleto();

        const writeStream = new WritableBufferStream();

        return new Observable((observer: Observer<NickProcessArtfact>) => {

            try {
                new GeradorDeBoleto(boleto).gerarPDF({
                    creditos: '',
                    stream: writeStream
                }, (err: any, pdf: any) => {
                    if (err) return console.error(err);

                    writeStream.on('finish', (dev: any) => {

                        const uploadArtifact = new NickProcessArtfact();
                        uploadArtifact.kind = ArtfactKind.ANY;
                        uploadArtifact.object = {
                            buffer: writeStream.toBuffer(),
                            contentType: 'application/pdf'
                        };

                        observer.next(uploadArtifact);
                        observer.complete();
                    });
                });
            } catch (e) {
                observer.error(e);
                observer.complete();
            }

        });
    }

    @Hook('ec3e4b16-21f7-4546-904c-a949c3005c0c')
    public shipment(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {

        // generate lot of services
        // const details: any[] = inputArtifact.object.data;
        const details: any[] = dados;

        const lots: any[] = [];
        let lotSequence = 0;
        for (let i = 0; i < details.length; i++) {
            const detail: any = details[i];

            if (!detail.field9 || !detail.field9.length)
                continue;

            const lot: any = {};

            ++lotSequence;

            lot.header_lote = {
                lote_servico: lotSequence,
                tipo_inscricao: '2',
                num_inscricao_empresa: '34127563000167',
                cod_trasmissao: '320100002507390',
                nome_empresa: 'CONSELHO NACIONAL DO BRASIL DA SOCIEDADE DE SAO VICENTE DE PAULO',
                mensagem_1: '',
                mensagem_2: '',
                numero_remessa: '7',
                data_gravacao: '27072019'
            };

            lot.detail_p = {
                lote_servico: lotSequence,
                num_sequencial: '1',
                cod_movimento: '01',
                agencia_destinataria: '3201',
                agencia_destinataria_digito: '',
                numero_conta_corrente: '13000215',
                digito_verif_conta_corrente: '8',
                conta_cob_destinataria: '13000215',
                conta_cob_destinataria_digito: '8',
                indentificacao_titulo_banco: `${detail.field18}${GeradorDeDigitoPadrao.mod11(detail.field18, {
                    de: [0, 1, 10, 11],
                    para: 0
                })}`,
                tipo_cobranca: '5',
                forma_cadastramento: '1',
                tipo_documento: '1',
                num_documento: `${detail.field18}`,
                data_vencimento: '20082019',
                valor_nominal: '1000',
                especie_titulo: '02',
                identificacao_titulo: 'N',
                data_emissao: '27072019',
                codigo_juros: '3',
                codigo_desconto: '0',
                indentificacao_titulo_empresa: `${detail.field18}`,
                codigo_protesto: '0',
                codigo_baixa: '3'
            };

            lot.detail_q = {
                lote_servico: lotSequence,
                num_sequencial: '2',
                cod_movimento: '01',
                tipo_inscricao: '2',
                num_inscricao: FormatacoesUtils.removerMascara(detail.field9),
                nome_pagador: `${detail.field21}`,
                endereco_pagador: `${detail.field22}, ${detail.field23}, ${detail.field24}`,
                bairro_pagador: detail.field11,
                cep_pagador: detail.field10.substr(0, detail.field10.length - 3),
                cep_pagador_sufixo: detail.field10.substr(detail.field10.length - 3, detail.field10.length),
                cidade_pagador: detail.field11,
                uf_pagador: detail.field12
            };

            lot.detail_r = {
                lote_servico: lotSequence,
                num_sequencial: '3',
                cod_movimento: '01',
                codigo_desconto_2: '0',
                data_desconto_2: '',
                valor_percentual_desconto_2: '',
                codigo_desconto_3: '0',
                data_desconto_3: '',
                valor_percentual_desconto_3: '',
                codigo_multa: '1',
                data_multa: '',
                valor_multa: '',
                mensagem_3: '',
                mensagem_4: ''
            };

            lot.detail_s = {
                lote_servico: lotSequence,
                num_sequencial: '4',
                cod_movimento: '01',
                identificacao_impressao: '1',
                numero_linha_impressa: '01',
                mensagem_recibo: '4',
                mensagem_impressa: 'COLETA PARA JUVENTUDE CONFORME A CIRCULAR 012/CNJ/2019 DE 12/05/2019 PAGAMENTO MÍNIMO R$ 10,00'
            };

            lot.trailer_lote = {
                lote_servico: lotSequence,
                qtd_registros_lote: '6'
            };

            lots.push(lot);

        }

        const filesLayout = {
            header_arquivo: {
                lote_servico: '0000',
                tipo_inscricao: '2',
                num_inscricao_empresa: '34127563000167',
                cod_trasmissao: '320100002507390',
                nome_empresa: 'CONSELHO NACIONAL DO BRASIL DA SOCIEDADE DE SAO VICENTE DE PAULO',
                data_geracao: '27072019',
                sequencial_arquivo: '7'
            },
            lots,
            trailer_arquivo: {
                lote_servico: '9999',
                qtd_registros_lote: `${lots.length}`,
                qtd_registros_arquivo: `${(lots.length * 6) + 2}`
            }
        };

        const finalresult = YamlGenerate.generateRemessaCnab(filesLayout, 240, YamlGenerate.BANK.santander.code);

        fs.writeFileSync('../temp/remessa.rem', finalresult);

        return of(inputArtifact);
    }

    @Hook('101bd4b2-3bb2-43a7-9e3c-e1e77ddddafd')
    public shipmentReturn(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {
                const shipmentReturns = YamlGenerate.BANK[inputArtifact.object.config.bank].retorno[inputArtifact.object.config.cnab];
                inputArtifact.object = YamlGenerate.generateRetornoCnab(YamlGenerate.BANK[inputArtifact.object.config.bank].retorno[inputArtifact.object.config.cnab], inputArtifact.object.config.cnab, YamlGenerate.BANK[inputArtifact.object.config.bank].code, inputArtifact.object.data, settings.environment);
                return of(inputArtifact);
            }));
    }

    @Hook('71ea5386-1726-4686-a5e6-3a6dbedb40a5')
    public boletoBradesco(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const createBoleto = () => {
            const pagador = createPagador();
            const beneficiario = createBeneficiario();
            const instrucoes = createInstrucoes();

            return Boleto.novoBoleto()
                .comDatas(Datas.novasDatas()
                    .comVencimento(inputArtifact.object.data.dueAt)
                    .comProcessamento(inputArtifact.object.data.generateAt)
                    .comDocumento(inputArtifact.object.data.generateAt))
                .comBeneficiario(beneficiario)
                .comPagador(pagador)
                .comBanco(new bancos.Bradesco())
                .comValorBoleto(parseFloat(inputArtifact.object.data.ammount)) // Apenas duas casas decimais
                .comNumeroDoDocumento(1001)
                .comEspecieDocumento('DM') // Duplicata de Venda Mercantil
                .comLocaisDePagamento(['Em qualquer banco até o vencimento'])
                .comInstrucoes(instrucoes);
        };

        const createPagador = () => {
            const enderecoPagador = Endereco.novoEndereco();
            enderecoPagador.primeiraLinha = `${inputArtifact.object.data.ownerAddress1}`;
            enderecoPagador.segundaLinha = `${inputArtifact.object.data.ownerAddress2}`;

            return Pagador.novoPagador()
                .comNome(`${inputArtifact.object.data.ownerName}`)
                .comRegistroNacional(`${inputArtifact.object.data.ownerDocument}`)
                .comEndereco(enderecoPagador);
        };

        const createBeneficiario = () => {
            const enderecoBeneficiario = Endereco.novoEndereco()
                .comLogradouro('RUA RIACHUELO, 75')
                .comBairro('CENTRO')
                .comCidade('RIO DE JANEIRO')
                .comUf('RJ')
                .comCep('20230-010');

            return Beneficiario.novoBeneficiario()
                .comNome('CONSELHO NACIONAL DO BRASIL DA S.S.V.P')
                .comRegistroNacional('34127563000167')
                .comCarteira('09')
                .comAgencia('1791')
                .comDigitoAgencia('4')
                .comCodigoBeneficiario('31550')
                .comDigitoCodigoBeneficiario('8')
                .comNossoNumero(inputArtifact.object.data.ourNumber.toString().padStart(11, '0')) // 11 -digitos // "00000005752"
                .comDigitoNossoNumero(GeradorDeDigitoPadrao.mod11(`09${inputArtifact.object.data.ourNumber.toString().padStart(11, '0')}`, bancos.Bradesco.susbstituicoes, bancos.Bradesco.series))
                .comEndereco(enderecoBeneficiario);
        };

        const createInstrucoes = () => {
            const instrucoes = [];
            instrucoes.push(`${inputArtifact.object.data.paymentInstruction}`);
            return instrucoes;
        };

        const boleto = createBoleto();

        const writeStream = new WritableBufferStream();

        return new Observable((observer: Observer<NickProcessArtfact>) => {

            try {
                new GeradorDeBoleto(boleto).gerarPDF({
                    creditos: '',
                    stream: writeStream
                }, (err: any, pdf: any) => {
                    if (err) return console.error(err);

                    writeStream.on('finish', (dev: any) => {

                        const uploadArtifact = new NickProcessArtfact();
                        uploadArtifact.kind = ArtfactKind.ANY;
                        uploadArtifact.object = {
                            buffer: writeStream.toBuffer(),
                            contentType: 'application/pdf'
                        };

                        observer.next(uploadArtifact);
                        observer.complete();
                    });
                });
            } catch (e) {
                observer.error(e);
                observer.complete();
            }

        });
    }

    @Hook('b9ed94af-126f-4324-8acd-1d1f1f8a8337')
    public shipmentBradesco(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {

        // generate lot of services
        // const details: any[] = inputArtifact.object.data;
        const details: any[] = inputArtifact.object.data;

        const lots: any[] = [];
        let lotSequence = 1;
        for (let i = 0; i < details.length; i++) {
            const detail: any = details[i];

            // if (!detail.field9 || !detail.field9.length)
            //     continue;

            const lot: any = {};

            ++lotSequence;

            const cep = detail.ownerAddress2.split(',')[0];

            lot.detail_type_1 = {
                identificacao_empresa: '00090179100315508',
                num_controle: detail.ourNumber,
                codigo_multa: '0',
                percentual_multa: '0',
                identificacao_titutlo: detail.ourNumber,
                digito_auto_conferencia: GeradorDeDigitoPadrao.mod11(`09${detail.ourNumber.toString().padStart(11, '0')}`, bancos.Bradesco.susbstituicoes, bancos.Bradesco.series).toString(),
                condicao_emissao: '2',
                identificacao_debito: 'N',
                identificacao_ocorrencia: '01',
                num_documento: detail.ourNumber,
                data_vencimento: moment(detail.dueAt).format('DDMMYY'),
                valor_titulo: parseFloat(detail.ammount).toFixed(2).replace('.', ''),
                especie_titulo: '01',
                data_emissao: moment(detail.generateAt).format('DDMMYY'),
                instricao_1: '00',
                instrucao_2: '00',
                tipo_inscricao: FormatacoesUtils.removerMascara(detail.ownerDocument).length == 11 ? '01' : '02',
                num_inscricao: FormatacoesUtils.removerMascara(detail.ownerDocument),
                nome_pagador: detail.ownerName,
                endereco_completo: `${detail.ownerAddress1} - ${detail.ownerAddress2} `,
                mensagem_1: '',
                cep: cep.substr(0, cep.length - 3),
                cep_sufixo: cep.substr(cep.length - 3, cep.length),
                sacador: detail.paymentInstruction,
                sequencial: lotSequence
            };
            lots.push(lot);

        }

        const filesLayout = {
            header_arquivo: {
                codigo_empresa: '4857169',
                nome_empresa: 'Conselho Nacional B.S.S.V.PA',
                data_arquivo: moment().format('DDMMYY'),
                numero_remessa: inputArtifact.object.config.lotSequence,
                numero_registro: '1'
            },
            lots,
            trailer_arquivo: {
                sequencial_registro: lots.length + 2
            }
        };

        const finalResult = YamlGenerate.generateRemessaCnab(filesLayout, 400, YamlGenerate.BANK.bradesco.code);

        const output = new NickProcessArtfact();
        output.kind = ArtfactKind.ANY;
        output.object = finalResult;

        return of(output);
    }

    @Hook('4a846e24-58e4-4bef-b109-bd784c01effe')
    public digitableLineToBarCodeFEBRABAN(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        let value = inputArtifact.object.value;

        const checkDigit = (digit: any) => {
            digit = digit.replace(/[^0-9]/g, '');
            let sum = 0;
            let weight = 2;
            const base = 9;
            const rest = 0;
            const contador = digit.length - 1;

            for (let i = contador; i >= 0; i--) {
                sum = sum + (digit.substring(i, i + 1) * weight);
                if (weight < base) {
                    weight++;
                } else {
                    weight = 2;
                }
            }
            let result = 11 - (sum % 11);
            if (result > 9) result = 0;
            if (result == 0) result = 1;
            return result;
        };

        const calculateBarcode = (line: any) => {
            let barcode = line.replace(/[^0-9]/g, '');

            if (checkDigit('34191000000000000001753980229122525005423000') != 1)
                throw new Error('Error converting digitableLine to barcode');

            if (barcode.length < 47) barcode = barcode + '00000000000'.substr(0, 47 - barcode.length);
            if (barcode.length != 47)
                throw new Error('Invalid digitableLine, conversion failed');

            barcode = barcode.substr(0, 4)
                + barcode.substr(32, 15)
                + barcode.substr(4, 5)
                + barcode.substr(10, 10)
                + barcode.substr(21, 10)
            ;
            if (checkDigit(barcode.substr(0, 4) + barcode.substr(5, 39)) != barcode.substr(4, 1))
                throw new Error('Error 5th digit ' + barcode.substr(4, 1) + ', the correct number is ' + checkDigit(barcode.substr(0, 4) + barcode.substr(5, 39)));
            return (barcode);
        };

        const digitableLineToBarcode = () => {
            const digitableLine = value;
            const barcode = calculateBarcode(value);
            value = barcode;
        };


        if (value.length % 2 !== 0) {
            digitableLineToBarcode();
        }

        const start = String.fromCharCode(201);
        const stop = String.fromCharCode(202);

        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = value.match(/.{2}/g).reduce((acc: any, part: any) => {
            const value = parseInt(part, 10);
            let ascii;

            if (value >= 0 && value <= 93) {
                ascii = value + 33;
            }

            if (value >= 94 && value <= 99) {
                ascii = value + 101;
            }

            return acc + String.fromCharCode(ascii);
        }, start) + stop;


        return of(outputArtifact);

    }
}
